import { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { Icon } from '@components/icon';
import { Body, Heading, HeadingProps } from '@components/text';
import { disableFocusableDescendants, restoreFocusableDescendants } from '@components/utils';

import './AccordionItem.scss';

const TABINDEX_BACKUP_ATTR = 'data-vds-accordion-orig-tabindex';

const isRenderableHeader = (header: React.ReactNode | undefined): boolean =>
  header !== undefined && header !== null && typeof header !== 'boolean';

export interface AccordionItemProps {
  children?: React.ReactNode;
  /** optional class */
  className?: string;
  /** Text label rendered in the default heading (and serving as the accessible name) when header is absent or non-renderable */
  label: string;
  /**
   * Optional rich header slot; takes precedence over label when renderable.
   * Rendered inside the toggle (`role="button"`), so it must contain
   * non-interactive content only — no links, buttons, or other focusable elements.
   * The toggle's accessible name derives from this content when provided.
   */
  header?: React.ReactNode;
  /** When false, children render without the Body caption wrapper */
  wrapContent?: boolean;
  /** Whether to prevent auto tracking. Default: false */
  preventAutoTracking?: boolean;
  /** If true, the content will be displayed */
  isActive?: boolean;
  /** Change size of layout for desktop and mobile. Default large */
  size?: 'large' | 'medium' | 'small';
  /** Semantic heading level for the question label. Default 3 (e.g. FAQ under a page h1). */
  headingLevel?: HeadingProps['ariaLevel'];
  /** id of the panel controlled by this item */
  ariaControls?: string;
  /** id of the label for this item */
  ariaLabelledBy?: string;
  /** Click handler for the accordion item */
  onClick?: () => void;
}

/**
 * AccordionItem component
 * @param children
 * @param className - optional class
 * @param label - Text label rendered (and used as accessible name) when header is absent or non-renderable
 * @param header - Optional rich header slot; non-interactive content only (renders inside the toggle button); include semantic headings via `Heading` `ariaLevel`
 * @param wrapContent - When false, children render without the Body caption wrapper
 * @param preventAutoTracking - Whether to prevent auto tracking. Default: false
 * @param isActive - If true, the content will be displayed
 * @param size - change size of layout for desktop and mobile. Default large
 * @param headingLevel - semantic heading level for the question label
 * @param ariaControls - id of the panel controlled by this item
 * @param ariaLabelledBy - id of the label for this item
 * @param onClick - Click handler for the accordion item
 */
export const AccordionItem: React.FC<AccordionItemProps> = ({
  className,
  size = 'large',
  headingLevel = 3,
  label,
  header,
  wrapContent = true,
  preventAutoTracking = false,
  isActive = false,
  children,
  onClick,
  ariaControls,
  ariaLabelledBy,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const hasCustomHeader = isRenderableHeader(header);

  useLayoutEffect(() => {
    const panel = contentRef.current;
    if (!panel) return;

    if (isActive) {
      panel.removeAttribute('inert');
      restoreFocusableDescendants(panel, TABINDEX_BACKUP_ATTR);
      return;
    }

    panel.setAttribute('inert', '');
    disableFocusableDescendants(panel, TABINDEX_BACKUP_ATTR);
  }, [isActive, children]);

  return (
    <div className={classNames('vds-accordion-item', className)}>
      <div
        className={classNames('vds-accordion-item__header', `vds-accordion-item__header--${size}`, {
          'vds-accordion-item__header--active': isActive,
          'vds-accordion-item__header--rich': hasCustomHeader,
        })}
        id={ariaLabelledBy}
        role="button"
        tabIndex={0}
        onClick={onClick}
        aria-controls={ariaControls}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        aria-expanded={isActive}
        {...(preventAutoTracking && {
          'data-prevent-auto-tracking': preventAutoTracking,
        })}>
        {hasCustomHeader ? (
          <div className="vds-accordion-item__header-content">{header}</div>
        ) : (
          <Heading
            className="vds-accordion-item__heading"
            weight="semibold"
            variant="heading-03"
            ariaLevel={headingLevel}
            color={isActive ? 'primary' : 'secondary'}
            size={size}>
            {label}
          </Heading>
        )}
        <Icon
          className="vds-accordion-item__chevron"
          name={isActive ? 'chevron-upward' : 'chevron-downward'}
          size={size === 'large' ? 'large' : 'medium'}
        />
      </div>
      <div
        ref={contentRef}
        id={ariaControls}
        className={classNames('vds-accordion-item__content', {
          'vds-accordion-item__content--active': isActive,
        })}
        aria-labelledby={ariaLabelledBy}
        aria-hidden={!isActive}>
        {wrapContent ? (
          <Body variant="caption" size={size} color="primary">
            {children}
          </Body>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
